import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  TextField,
  Paper,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Autocomplete,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  TableContainer
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteIcon from "@mui/icons-material/Delete";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useMaterialUIController } from "context";
import { useTheme } from "@mui/material/styles";

async function searchPlacesWithTariffs(query, { signal, limit = 10, skip = 0 } = {}) {
  if (!query || !query.trim()) return [];
//   https://blue-parking-invite.bluept.dev/api
//   http://localhost:4001/api
  const url = `https://blue-parking-invite.bluept.dev/api/search-places-with-tariffs?filter=${encodeURIComponent(query)}&limit=${limit}&skip=${skip}`;
  const res = await fetch(url, { method: "GET", signal });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    console.error("searchPlacesWithTariffs failed", res.status, txt);
    return [];
  }
  const json = await res.json();
  const list = json?.places || [];
  if (!Array.isArray(list)) return [];
  return list.map((it) => ({ id: it.id ?? it._id ?? it.placeId,
    name: it.name ,
    tariffs: it.tariffs || []
  }));
}

function expandToMinutes(ranges, maxMinute = 1440) {
  if (!Array.isArray(ranges) || ranges.length === 0) return [];
  const sorted = [...ranges]
    .map((r) => ({ from: Number(r.from), to: r.to == null ? null : Number(r.to), price: Number(r.price) }))
    .sort((a, b) => a.from - b.from);
  const rows = [];
  let highest = 0;
  for (const rng of sorted) highest = Math.max(highest, rng.to || highest);
  const target = sorted.some((r) => r.to == null) ? maxMinute : Math.min(Math.max(highest, 1), maxMinute);

  for (let m = 1; m <= target; m++) {
    const rng = sorted.find((r) => {
      const toVal = r.to == null ? maxMinute : r.to;
      return m >= r.from && m <= toVal;
    });

    if (!rng) {
      rows.push({ minutes: m, cost: 0 });
      continue;
    }

    // If this range is open-ended (to === null) push a single entry with the range's "from" and cost, then stop
    if (rng.to == null) {
      rows.push({ minutes: 1, cost: rng.price });
      break;
    }

    // Otherwise push per-minute row as before
    rows.push({ minutes: 1, cost: rng.price });
  }

  return rows;
}

export default function Tarrifs() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null); // {id,name}
  const [ranges, setRanges] = useState([]);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });
  const searchTimer = useRef(null);
  const [selectedTariffId, setSelectedTariffId] = useState("");
  const selectedTariff =
   selected && Array.isArray(selected.tariffs) && selectedTariffId
     ? selected.tariffs.find((t) => (t._id ?? t.id) === selectedTariffId) || null
     : null;
 const [controller] = useMaterialUIController();
 const { darkMode } = controller;
 const pageBackground = darkMode ? "#1A2035" : "#F5F5F5";
 const textColor = darkMode ? "#ffffff" : "#000000";
 const paperBg = darkMode ? "#27293d" : "#ffffff";
 const inputBorderColor = darkMode ? "#666" : "#ccc";

  useEffect(() => {
    clearTimeout(searchTimer.current);
    if (!query || !query.trim()) {
      setResults([]);
      return;
    }
    const controller = new AbortController();
    searchTimer.current = setTimeout(() => {
      searchPlacesWithTariffs(query, { signal: controller.signal })
        .then((list) => setResults(list))
        .catch((err) => {
          if (err.name === "AbortError") return;
          console.error("places search failed", err);
          setResults([]);
        });
    }, 300);
    return () => {
      clearTimeout(searchTimer.current);
      controller.abort();
    };
  }, [query]);

  const addRange = () =>
    setRanges((s) => {
      if (!Array.isArray(s) || s.length === 0) {
        return [{ from: 1, to: null, price: 0 }];
      }
      const last = s[s.length - 1];
      const lastEnd = last.to != null ? Number(last.to) : Number(last.from ?? 1);
      return [...s, { from: lastEnd + 1, to: null, price: 0 }];
    });
  const updateRange = (idx, key, value) => setRanges((s) => s.map((r, i) => (i === idx ? { ...r, [key]: value } : r)));
  const removeRange = (idx) => setRanges((s) => s.filter((_, i) => i !== idx));

  const handleSelect = (place) => {
    if (!place) return;
    const src =  place;
    setSelected(src);
    setSelectedTariffId("");
    setRanges([]);
  };

   const handleTariffSelect = (tariffId) => {
    setSelectedTariffId(tariffId);
  };



  const previewPal = () => {

    if (!ranges || ranges.length === 0) {
      alert("You must define at least one range before sending.");
      return false;
    }
    
    for (const r of ranges) {
      if (!Number.isFinite(Number(r.from)) || Number(r.from) <= 0) {
        alert("Each range must have a valid 'from' minute > 0.");
        return false;
      }
      if (r.to != null && Number(r.to) < Number(r.from)) {
        alert("'to' must be >= from or empty for open range.");
        return false;
      }
      if (!Number.isFinite(Number(r.price))) {
        alert("Each range must have a valid numeric price.");
        return false;
      }
    }

    return true
  };

  const sendToPal = async () => {
    if (!selected) {
      setToast({ open: true, severity: "warning", message: "Select a parking first." });
      return;
    }
    const valid = previewPal(); // validates and sets preview
    if (!valid) {
      // validation failed — do not send to backend
      setToast({ open: true, severity: "warning", message: "Validation failed — fix ranges first." });
      return;
    }
    const rows = expandToMinutes(ranges, 1440);
    const finalJson = {
        ...selectedTariff,
        tariff: rows
    }
    try {
        //   https://blue-parking-invite.bluept.dev/api
        //   http://localhost:4001/api
        const url = 'https://blue-parking-invite.bluept.dev/api/update-tariffs';
        const res = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...finalJson }),
      });

      const text = await res.text().catch(() => "");
      let json = null;
      try { json = text ? JSON.parse(text) : null; } catch (e) { json = text; }
      if (!res.ok) {
        const msg = (json && json.details) || (json && json.error) || `Status ${res.status}`;
        setToast({ open: true, severity: "error", message: `Update failed: ${msg}` });
        console.error("PAL update failed", res.status, json);
        return;
      }
      const palMsg = json?.pal?.msg || json?.pal?.message || json?.pal || "Tariff updated";
      setSelectedTariffId("");
      setRanges([]);
      setToast({ open: true, severity: "success", message: String(palMsg) });
    } catch (e) {
      console.error(e);
      setToast({ open: true, severity: "error", message: "Failed to send to PAL. See console." });
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box 
      p={2}>
       <Snackbar
         open={toast.open}
         autoHideDuration={5000}
         onClose={() => setToast((t) => ({ ...t, open: false }))}
         anchorOrigin={{ vertical: "top", horizontal: "left" }}
       >
         <Alert
           onClose={() => setToast((t) => ({ ...t, open: false }))}
           severity={toast.severity}
           sx={{ width: "100%", fontSize: "1.5rem" }}
         >
           {toast.message}
         </Alert>
       </Snackbar>
        <Typography variant="h6" gutterBottom>
          Search Parking
        </Typography>

        <Autocomplete
          freeSolo
          openOnFocus
          options={results}
          getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt.name || opt.id || "")}
          filterOptions={(options, { inputValue }) => {
           const q = (inputValue || "").trim().toLowerCase();
           if (!q) return options;
           return options.filter((o) => {
             const name = String(o.name || "").toLowerCase();
             const id = String(o.id || "").toLowerCase();
             return name.includes(q) || id.includes(q);
           });
         }}
          inputValue={query}
          onInputChange={(event, value, reason) => {
            setQuery(value);
            if (!value || !value.trim()) {
              setResults([]);
              setSelected(null);
              setSelectedTariffId("");
              setRanges([]);
              return;
            }
            if (reason === "clear") setResults([]);
          }}
          onChange={(_, value) => {
            if (!value) return;
            if (typeof value === "string") {
              const match = results.find((r) => r.name === value);
              if (match) handleSelect(match);
              return;
            }
            handleSelect(value);
          }}
          renderInput={(params) => (
            <TextField {...params} placeholder="Type name or id..." fullWidth sx={{ mb: 2 }} />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <ListItemText primary={option.name} secondary={option.id} />
            </li>
          )}
        />

        {selected && Array.isArray(selected.tariffs) && selected.tariffs.length > 0 && (
         <Box sx={{ mt: 2, mb:3, display: "flex", gap: 1, alignItems: "center" }}>
           <FormControl size="small" sx={{ minWidth: 260 }}>
             <InputLabel id="tariff-select-label">Tariff</InputLabel>
             <Select
               labelId="tariff-select-label"
               value={selectedTariffId}
               label="Tariff"
               onChange={(e) => handleTariffSelect(e.target.value)}
             >
               {selected.tariffs.map((t) => (
                 <MenuItem onClick={() => setRanges([])} key={t._id ?? t.id} value={t._id ?? t.id}>
                   {t.name ?? t._id ?? t.id} - {t._id ?? t.id}
                 </MenuItem>
               ))}
             </Select>
           </FormControl>
         </Box>
        )}
        
        {selected && selectedTariffId && (
          <>
            <Typography sx={{mt:5, mb:1}} color={textColor} variant="subtitle1" gutterBottom>
              Editing tariffs for id {selectedTariffId} : {selected.name}
             {selectedTariff ? ` — ${selectedTariff.name || selectedTariff._id}` : ""}
            </Typography>
            <Paper  variant="outlined" sx={{ p: 1, mb: 2, bgcolor: paperBg, color: textColor }}>
              <Table 
                sx={{
                    color: textColor,
                    "& .MuiTextField-root": {
                    width: "50%",
                    }
                }}
                size="medium">
                <TableHead sx={{ display: "table-header-group" }}>
                  <TableRow>
                    <TableCell sx={{ color: textColor }}>From (min)</TableCell>
                    <TableCell sx={{ color: textColor }}>To (min or empty)</TableCell>
                    <TableCell sx={{ color: textColor }}>Price (NIS / min)</TableCell>
                    <TableCell sx={{ color: textColor }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ranges.map((r, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <TextField
                          type="number"
                          size="medium"
                          value={r.from}
                          onChange={(e) => updateRange(idx, "from", Number(e.target.value))}
                          
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          size="medium"
                          value={r.to == null ? "" : r.to}
                          onChange={(e) => updateRange(idx, "to", e.target.value === "" ? null : Number(e.target.value))}
                          placeholder="open"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          size="medium"
                          value={r.price}
                          onChange={(e) => updateRange(idx, "price", Number(e.target.value))}
                          inputProps={{ step: "0.01" }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" color="error" onClick={() => removeRange(idx)}>
                          <DeleteIcon  />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Box sx={{  mt: 1, display: "flex", alignItems: "center"  }}>
                <Button startIcon={<AddIcon />} onClick={addRange} sx={{ mr: 1 }}>
                  Add range
                </Button>
                <Button variant="contained" color="primary" onClick={sendToPal}>
                  Update Tariffs
                </Button>

                <Button
                  startIcon={<ClearIcon />}
                  onClick={() => setSelectedTariffId("")}
                  variant="contained"
                  sx={{ ml: "auto",
                        backgroundColor: '#d32f2f',
                        '&:hover': { backgroundColor: '#b71c1c' } 
                      }}
                >
                  Clear
                </Button>
              </Box>
              
            </Paper>
          </>
        )}
      </Box>
    </DashboardLayout>
  );
}